import moment from 'moment';
import 'moment-timezone';

/** Inclusive day count for week-style booking calendar grid (calendar / full / room views). */
export const CALENDAR_GRID_DAY_COUNT = 7;

export const CALENDAR_GRID_DAY_OPTIONS = [7, 15, 30];

export const CALENDAR_GRID_DAY_COUNT_STORAGE_KEY = 'roomnexa_dashboard_grid_day_count';

export function readStoredGridDayCount(fallback = CALENDAR_GRID_DAY_COUNT) {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(CALENDAR_GRID_DAY_COUNT_STORAGE_KEY);
    const parsed = Number(raw);
    return CALENDAR_GRID_DAY_OPTIONS.includes(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

export function writeStoredGridDayCount(count) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CALENDAR_GRID_DAY_COUNT_STORAGE_KEY, String(count));
  } catch {
    // ignore
  }
}

export function getGridCellMinWidth(dayCount = CALENDAR_GRID_DAY_COUNT) {
  if (dayCount <= 7) return 96;
  if (dayCount <= 15) return 72;
  return 52;
}

/** Fixed flex basis for workspace grid day columns (7 / 15 / 30). */
export function getGridDayColumnSx(dayCount = CALENDAR_GRID_DAY_COUNT) {
  const width = getGridCellMinWidth(dayCount);
  return {
    flex: `0 0 ${width}px`,
    width: `${width}px`,
    minWidth: `${width}px`,
    maxWidth: `${width}px`,
  };
}

/** Minimum scrollable width for workspace grid (room label + all day columns). */
export function getGridScrollMinWidth(dayCount, roomLabelWidth = 108) {
  const safeDayCount = Math.max(1, Number(dayCount) || CALENDAR_GRID_DAY_COUNT);
  return roomLabelWidth + safeDayCount * getGridCellMinWidth(safeDayCount);
}

/** Booking bar + channel logo sizing for workspace grid (7 / 15 / 30 day views). */
export function getGridBarMetrics(dayCount = CALENDAR_GRID_DAY_COUNT) {
  if (dayCount <= 7) {
    return {
      barHeight: 28,
      barGap: 2,
      barTopOffset: 2,
      logoSize: 18,
      logoOutdent: 0,
      pillPadExtra: 4,
      labelFontSize: '0.72rem',
      actionIconSize: '0.72rem',
      pillBorderRadius: '12px',
      pillBorderWidth: 2,
      hideActionsWhenSpanAtMost: 0,
    };
  }
  if (dayCount <= 15) {
    return {
      barHeight: 24,
      barGap: 2,
      barTopOffset: 2,
      logoSize: 16,
      logoOutdent: 0,
      pillPadExtra: 3,
      labelFontSize: '0.64rem',
      actionIconSize: '0.62rem',
      pillBorderRadius: '10px',
      pillBorderWidth: 2,
      hideActionsWhenSpanAtMost: 1,
    };
  }
  return {
    barHeight: 20,
    barGap: 2,
    barTopOffset: 2,
    logoSize: 14,
    logoOutdent: 0,
    pillPadExtra: 2,
    labelFontSize: '0.56rem',
    actionIconSize: '0.52rem',
    pillBorderRadius: '8px',
    pillBorderWidth: 1.5,
    hideActionsWhenSpanAtMost: 2,
  };
}

/**
 * Generate date range array from start to end date (inclusive)
 * @param {string|Date|moment} startDate - Start date
 * @param {string|Date|moment} endDate - End date
 * @returns {Array} Array of date objects with formatted information
 */
export const generateDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return [];
  
  const start = moment(startDate);
  const end = moment(endDate);
  const dates = [];
  let current = start.clone();
  
  while (current.isSameOrBefore(end, 'day')) {
    dates.push({
      key: current.format('YYYY-MM-DD'),
      date: current.clone(),
      display: formatHotelDate(current),
      day: current.format('ddd'),
      month: current.format('MMM'),
      dayNum: current.format('DD'),
      year: current.format('YYYY'),
    });
    current.add(1, 'day');
  }
  
  return dates;
};

/**
 * Get today's date as YYYY-MM-DD string
 */
export const getTodayDateString = () => {
  return moment().format('YYYY-MM-DD');
};

/** API / storage format — always ISO date for requests and business logic. */
export const HOTEL_DATE_API_FORMAT = 'YYYY-MM-DD';

export const DEFAULT_HOTEL_DATE_FORMAT = 'DD/MM/YYYY';

const HOTEL_DATE_FORMAT_BY_COUNTRY = {
  IN: 'DD/MM/YYYY',
  GB: 'DD/MM/YYYY',
  AU: 'DD/MM/YYYY',
  NZ: 'DD/MM/YYYY',
  US: 'MM/DD/YYYY',
};

export const getSelectedHotelContext = () => {
  const djangoContext = typeof window !== 'undefined' ? window.DJANGO_CONTEXT || {} : {};
  return djangoContext.selectedHotel || {};
};

/** Display date format from hotel config (falls back to country, then DD/MM/YYYY). */
export const getHotelDateFormat = () => {
  const hotel = getSelectedHotelContext();
  if (hotel.date_format && String(hotel.date_format).trim()) {
    return hotel.date_format;
  }
  if (hotel.country && HOTEL_DATE_FORMAT_BY_COUNTRY[hotel.country]) {
    return HOTEL_DATE_FORMAT_BY_COUNTRY[hotel.country];
  }
  return DEFAULT_HOTEL_DATE_FORMAT;
};

/** Date + time display for pickers and timestamps in hotel timezone. */
export const getHotelDateTimeFormat = () => `${getHotelDateFormat()} hh:mm A`;

/**
 * Parse user-entered or API date into moment (start of day).
 * Accepts hotel display format and YYYY-MM-DD.
 */
export const parseHotelDate = (value) => {
  if (!value) return null;
  if (moment.isMoment(value)) return value.clone().startOf('day');
  const fmt = getHotelDateFormat();
  const parsed = moment(value, [fmt, HOTEL_DATE_API_FORMAT, moment.ISO_8601], true);
  if (!parsed.isValid()) return null;
  return parsed.startOf('day');
};

/** Format any date value for UI display using hotel date format. */
export const formatHotelDate = (value, formatOverride) => {
  if (value == null || value === '') return '';
  const fmt = formatOverride || getHotelDateFormat();
  const m = moment.isMoment(value)
    ? value.clone()
    : moment(value, [HOTEL_DATE_API_FORMAT, fmt, moment.ISO_8601], true);
  return m.isValid() ? m.format(fmt) : String(value);
};

/** Format ISO datetime in hotel timezone with hotel date + time format. */
export const formatHotelDateTime = (value, hotelTimezone = getHotelTimezone()) => {
  if (!value) return '';
  const m = moment.utc(value);
  if (!m.isValid()) return typeof value === 'string' ? value : '';
  const tz = (hotelTimezone && String(hotelTimezone).trim()) || getHotelTimezone() || 'UTC';
  const zoned = moment.tz ? m.tz(tz) : m.utc();
  return zoned.format(getHotelDateTimeFormat());
};

/** Split an ISO datetime into hotel-local date + time parts for chips and labels. */
export const formatHotelDateTimeParts = (value, hotelTimezone = getHotelTimezone()) => {
  if (!value) return null;
  const m = moment.utc(value);
  if (!m.isValid()) return null;
  const tz = (hotelTimezone && String(hotelTimezone).trim()) || getHotelTimezone() || 'UTC';
  const zoned = moment.tz ? m.clone().tz(tz) : m.clone().utc();
  const date = zoned.format(getHotelDateFormat());
  const time = zoned.format('hh:mm A');
  return { date, time, full: `${date}, ${time}` };
};

/**
 * Convert a UTC/ISO timestamp into a naive local moment carrying the hotel wall-clock.
 * Used with MUI AdapterMoment pickers that are not timezone-aware.
 */
export const toHotelPickerMoment = (value, hotelTimezone = getHotelTimezone()) => {
  if (!value) return null;
  if (moment.isMoment(value)) {
    if (!value.isValid()) return null;
    const tz = (hotelTimezone && String(hotelTimezone).trim()) || null;
    if (tz && moment.tz) {
      const zoned = value.clone().tz(tz);
      return moment({
        year: zoned.year(),
        month: zoned.month(),
        date: zoned.date(),
        hour: zoned.hour(),
        minute: zoned.minute(),
        second: zoned.second(),
        millisecond: 0,
      });
    }
    return value.clone().local();
  }
  const m = moment.utc(value);
  if (!m.isValid()) return null;
  const tz = (hotelTimezone && String(hotelTimezone).trim()) || null;
  const zoned = tz && moment.tz ? m.tz(tz) : m.local();
  return moment({
    year: zoned.year(),
    month: zoned.month(),
    date: zoned.date(),
    hour: zoned.hour(),
    minute: zoned.minute(),
    second: zoned.second(),
    millisecond: 0,
  });
};

/**
 * Interpret a picker moment's wall-clock as hotel-local time and return UTC ISO.
 */
export const fromHotelPickerMomentToIso = (momentValue, hotelTimezone = getHotelTimezone()) => {
  if (!momentValue || !moment.isMoment(momentValue) || !momentValue.isValid()) {
    return null;
  }
  const tz = (hotelTimezone && String(hotelTimezone).trim()) || null;
  if (tz && moment.tz) {
    const wall = momentValue.format('YYYY-MM-DD HH:mm:ss');
    return moment.tz(wall, 'YYYY-MM-DD HH:mm:ss', tz).toISOString();
  }
  return momentValue.toISOString();
};

/** Convert picker/moment/display input to YYYY-MM-DD for API payloads. */
export const toApiDateString = (value) => {
  if (!value) return '';
  const m = moment.isMoment(value) ? value : parseHotelDate(value);
  return m && m.isValid() ? m.format(HOTEL_DATE_API_FORMAT) : '';
};

/**
 * Selected hotel IANA timezone from hotel configuration only.
 * Never uses the browser/local timezone — falls back to UTC if unset.
 */
export const getHotelTimezone = () => {
  const selectedHotel = getSelectedHotelContext();
  const configured = selectedHotel.timezone && String(selectedHotel.timezone).trim();
  return configured || 'UTC';
};

/** Current date-time in hotel config timezone (UTC if unset). */
export const getHotelNowMoment = (timezone = getHotelTimezone()) => {
  const tz = (timezone && String(timezone).trim()) || 'UTC';
  if (moment.tz) {
    return moment.tz(tz);
  }
  return moment.utc();
};

/** Today as YYYY-MM-DD in hotel config timezone (UTC if unset). */
export const getHotelTodayDateString = (timezone = getHotelTimezone()) => {
  const tz = (timezone && String(timezone).trim()) || 'UTC';
  if (moment.tz) {
    return moment.tz(tz).format('YYYY-MM-DD');
  }
  return moment.utc().format('YYYY-MM-DD');
};

/**
 * Quick book is only allowed for today or future dates (hotel timezone).
 * @param {string|Date|moment} checkInDate
 * @param {string} [timezone]
 */
export const isQuickBookDateAllowed = (checkInDate, timezone = getHotelTimezone()) => {
  if (!checkInDate) return false;
  const checkIn =
    timezone && moment.tz
      ? moment.tz(checkInDate, ['YYYY-MM-DD', moment.ISO_8601], timezone)
      : moment(checkInDate, 'YYYY-MM-DD', true);
  if (!checkIn.isValid()) return false;
  const today =
    timezone && moment.tz ? moment.tz(timezone).startOf('day') : moment().startOf('day');
  return checkIn.startOf('day').isSameOrAfter(today, 'day');
};

/**
 * Convert Date object to moment, ensuring correct date interpretation
 */
export const dateToMoment = (date) => {
  if (!date) return null;
  const dateStr = moment(date).format('YYYY-MM-DD');
  return moment(dateStr, 'YYYY-MM-DD', true).startOf('day');
};

/**
 * Convert moment to Date object using date components
 */
export const momentToDate = (momentObj) => {
  if (!momentObj || !moment.isMoment(momentObj)) return null;
  const dateStr = momentObj.format('YYYY-MM-DD');
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

/**
 * Find column index for a date in date range
 * @param {Array} dateRange - Array of date objects
 * @param {moment} targetDate - Target date to find
 * @param {boolean} startsBefore - Whether the date starts before the range
 * @returns {number} Column index, or -1 if not found
 */
export const findDateColumn = (dateRange, targetDate, startsBefore = false) => {
  if (startsBefore) return 0;
  if (!dateRange?.length || !targetDate?.isValid?.()) return -1;

  const index = dateRange.findIndex(d => {
    const dateMoment = moment(d.key);
    return dateMoment.isSame(targetDate, 'day') || dateMoment.isAfter(targetDate, 'day');
  });

  // Target is entirely after the visible range — caller must skip the bar.
  return index;
};

/**
 * Find end column index for a date in date range
 * If the date extends beyond the calendar, returns the last column index
 * For visual display, checkout date is inclusive (bar extends to middle of checkout date column)
 * This ensures bars show from middle of check-in date to middle of check-out date
 * Example: booking 28th to 31st should show from middle of 28th to middle of 31st (4 columns)
 * @param {Array} dateRange - Array of date objects
 * @param {moment} targetDate - Target date to find (checkout date)
 * @param {moment} checkInDate - Optional check-in date (for reference, not used for calculation)
 * @returns {Object} { endCol: number, extendsBeyondCalendar: boolean }
 */
export const findEndDateColumn = (dateRange, targetDate, checkInDate = null) => {
  if (!dateRange || dateRange.length === 0) {
    return { endCol: -1, extendsBeyondCalendar: false };
  }
  
  const lastDateMoment = moment(dateRange[dateRange.length - 1].key);
  
  // If target date is after the last date in calendar, extend to the end
  if (targetDate.isAfter(lastDateMoment, 'day')) {
    return { endCol: dateRange.length - 1, extendsBeyondCalendar: true };
  }
  
  // For visual display, checkout date is inclusive (bar extends to middle of checkout date column)
  // Find the column that matches the checkout date
  // This ensures bars show from middle of check-in date to middle of check-out date
  for (let i = 0; i < dateRange.length; i++) {
    const dateMoment = moment(dateRange[i].key);
    // Find the column that matches checkout date
    if (dateMoment.isSame(targetDate, 'day')) {
      return { endCol: i, extendsBeyondCalendar: false };
    }
    // If we've passed the checkout date, use the previous column
    if (dateMoment.isAfter(targetDate, 'day')) {
      return { endCol: i > 0 ? i - 1 : 0, extendsBeyondCalendar: false };
    }
  }
  
  // If checkout date is at or after the last date in calendar, use the last column
  return { endCol: dateRange.length - 1, extendsBeyondCalendar: false };
};

/** Pixel floor so ultra-short stays (e.g. 10 minutes) stay visible and hoverable. */
export const MIN_BOOKING_BAR_WIDTH_PX = 10;

/**
 * Keep short bars at least MIN_BOOKING_BAR_WIDTH_PX wide for hit-testing / hover.
 * Full-bleed spans already exceed the floor; max() is harmless for them.
 */
const withMinBarWidth = (barWidthCss) => (
  `max(${MIN_BOOKING_BAR_WIDTH_PX}px, ${barWidthCss})`
);

/**
 * Calculate booking bar position and width.
 * Default: mid check-in → mid check-out so same-day turnover can share a row.
 * Optional startDayFraction / endDayFraction (0–1) place ends at actual clock times.
 * At the visible calendar start/end, inset slightly so pills never bleed over the
 * room-column border or the last grid line.
 */
export const calculateBarPosition = (
  startCol,
  span,
  dateRangeLength,
  startsBeforeCalendar,
  extendsBeyondCalendar = false,
  roomColumnWidth = 0,
  startDayFraction = null,
  endDayFraction = null,
) => {
  const safeDays = Math.max(1, Number(dateRangeLength) || 1);
  const columnWidth = `(100% - ${roomColumnWidth}px) / ${safeDays}`;
  const halfColumnWidth = `(100% - ${roomColumnWidth}px) / ${safeDays} / 2`;
  const edgeInsetPx = 3;
  const meetGapPx = 2; // keep rounded ends off the vertical grid line at mid-day meets
  // Same-day short stays need a usable visual width (~12% of the day column).
  const minSameDayFraction = 0.12;
  const endCol = startCol + Math.max(1, Number(span) || 1) - 1;
  const isLastVisibleCol = extendsBeyondCalendar || endCol >= safeDays - 1;

  const hasCustomStart = startDayFraction != null && Number.isFinite(Number(startDayFraction));
  const hasCustomEnd = endDayFraction != null && Number.isFinite(Number(endDayFraction));

  if (hasCustomStart || hasCustomEnd) {
    let startF = hasCustomStart ? Number(startDayFraction) : 0.5;
    let endF = hasCustomEnd ? Number(endDayFraction) : (endCol === startCol ? 1 : 0.5);
    startF = Math.min(0.98, Math.max(0.02, startF));
    endF = Math.min(0.98, Math.max(0.02, endF));
    // Only bump when CI/CO fractions apply to the same *visible* day.
    // If the stay starts before the calendar, startF is from an off-screen day
    // (e.g. 4:56pm CI) and must not be compared to today's CO (e.g. 9:41am) —
    // that incorrectly stretched morning checkouts to ~80% of the column.
    // Also expand 10-minute (etc.) stays so the bar is hoverable, not a 1px line.
    if (!startsBeforeCalendar && endCol === startCol) {
      if (endF - startF < minSameDayFraction) {
        endF = Math.min(0.98, startF + minSameDayFraction);
        if (endF - startF < minSameDayFraction) {
          startF = Math.max(0.02, endF - minSameDayFraction);
        }
      }
    }

    const startOffset = startsBeforeCalendar ? 0 : startCol + startF;
    const endOffset = extendsBeyondCalendar ? safeDays : endCol + endF;

    let leftOffset;
    let barWidth;
    if (startsBeforeCalendar) {
      leftOffset = `calc(${roomColumnWidth}px + ${edgeInsetPx}px)`;
      if (extendsBeyondCalendar) {
        barWidth = `calc(100% - ${roomColumnWidth}px - ${edgeInsetPx * 2}px)`;
      } else {
        const endInset = isLastVisibleCol ? edgeInsetPx + meetGapPx : meetGapPx;
        barWidth = `calc(${endOffset} * ${columnWidth} - ${edgeInsetPx + endInset}px)`;
      }
    } else {
      leftOffset = `calc(${roomColumnWidth}px + ${startOffset} * ${columnWidth} + ${meetGapPx}px)`;
      if (extendsBeyondCalendar) {
        barWidth = `calc(100% - ${roomColumnWidth}px - ${startOffset} * ${columnWidth} - ${edgeInsetPx + meetGapPx}px)`;
      } else {
        const endInset = isLastVisibleCol ? edgeInsetPx + meetGapPx : meetGapPx;
        barWidth = `calc((${endOffset} - ${startOffset}) * ${columnWidth} - ${meetGapPx + endInset}px)`;
      }
    }
    return { leftOffset, barWidth: withMinBarWidth(barWidth) };
  }

  let leftOffset;
  let barWidth;

  if (startsBeforeCalendar) {
    // Flush to calendar start (with inset), end at mid checkout column (or full end).
    leftOffset = `calc(${roomColumnWidth}px + ${edgeInsetPx}px)`;
    if (extendsBeyondCalendar) {
      barWidth = `calc(100% - ${roomColumnWidth}px - ${edgeInsetPx * 2}px)`;
    } else {
      barWidth = `calc(${endCol} * ${columnWidth} + ${halfColumnWidth} - ${edgeInsetPx + meetGapPx}px)`;
    }
  } else if (span === 1) {
    // Single visible day: second half so a departing same-day bar can use the first half.
    leftOffset = `calc(${roomColumnWidth}px + ${startCol} * ${columnWidth} + ${halfColumnWidth} + ${meetGapPx}px)`;
    if (extendsBeyondCalendar) {
      barWidth = `calc(100% - ${roomColumnWidth}px - ${startCol} * ${columnWidth} - ${halfColumnWidth} - ${edgeInsetPx + meetGapPx}px)`;
    } else if (isLastVisibleCol) {
      barWidth = `calc(${halfColumnWidth} - ${edgeInsetPx + meetGapPx}px)`;
    } else {
      barWidth = `calc(${halfColumnWidth} - ${meetGapPx * 2}px)`;
    }
  } else {
    // Multi-day: mid check-in → mid check-out.
    leftOffset = `calc(${roomColumnWidth}px + ${startCol} * ${columnWidth} + ${halfColumnWidth} + ${meetGapPx}px)`;
    if (extendsBeyondCalendar) {
      barWidth = `calc(100% - ${roomColumnWidth}px - ${startCol} * ${columnWidth} - ${halfColumnWidth} - ${edgeInsetPx + meetGapPx}px)`;
    } else {
      const endInset = isLastVisibleCol ? edgeInsetPx + meetGapPx : meetGapPx;
      barWidth = `calc((${endCol} - ${startCol}) * ${columnWidth} - ${meetGapPx + endInset}px)`;
    }
  }

  return { leftOffset, barWidth: withMinBarWidth(barWidth) };
};

/**
 * Format payment timestamp for display in hotel timezone.
 * Backend sends processed_at/created_at as ISO UTC; show in hotel TZ when available.
 * @param {string|Date} dateInput - ISO date string or Date (e.g. payment.processed_at)
 * @param {string} [hotelTimezone] - IANA timezone (e.g. 'Asia/Kolkata'). If missing, uses local.
 * @returns {string} Formatted string 'DD MMM YYYY, hh:mm A' or raw input / 'N/A'
 */
export const formatPaymentTime = (dateInput, hotelTimezone) => {
  if (!dateInput) return 'N/A';
  const m = moment.utc(dateInput);
  if (!m.isValid()) return typeof dateInput === 'string' ? dateInput : 'N/A';
  const tz = (hotelTimezone && String(hotelTimezone).trim()) || null;
  if (tz && moment.tz) {
    return m.tz(tz).format(getHotelDateTimeFormat());
  }
  return m.local().format(getHotelDateTimeFormat());
};

/**
 * Serialize a moment from the payment date picker for API (processed_at).
 * Returns undefined when the value is missing or invalid.
 */
export const toPaymentProcessedAtIso = (momentValue) => {
  if (!momentValue || !moment.isMoment(momentValue) || !momentValue.isValid()) {
    return undefined;
  }
  return momentValue.toISOString();
};
